/**
 * 1.67 (193) — this device's Web Push subscription: its state, turning it on (only from a button — a permission
 * prompt is never shown on open), re-sending it on open when permission is already granted, and forgetting it
 * before a sign-out (so a phone handed to the next waiter never rings for the last one).
 * Light on purpose: the shell imports it (through sign-out) — no web-push crypto here.
 */
export type PushState = 'unsupported' | 'denied' | 'off' | 'on'

function supported(): boolean {
  return typeof window !== 'undefined' && typeof navigator !== 'undefined'
    && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window
}

async function registration(): Promise<ServiceWorkerRegistration | null> {
  try { return (await navigator.serviceWorker.getRegistration()) ?? null } catch { return null }
}

function keyBytes(b64: string): Uint8Array {
  const t = b64.replace(/-/g, '+').replace(/_/g, '/')
  const bin = atob(t + '='.repeat((4 - (t.length % 4)) % 4))
  return Uint8Array.from(bin, (c) => c.charCodeAt(0))
}

async function post(body: Record<string, unknown>): Promise<boolean> {
  try {
    const r = await fetch('/api/push', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
    return r.ok
  } catch { return false }
}

async function send(sub: PushSubscription, lang: 'ka' | 'ru'): Promise<boolean> {
  const j = sub.toJSON()
  if (!j.endpoint || !j.keys?.p256dh || !j.keys?.auth) return false
  return post({ action: 'subscribe', endpoint: j.endpoint, p256dh: j.keys.p256dh, auth: j.keys.auth, lang })
}

function sameKey(a: ArrayBuffer | null | undefined, b: Uint8Array): boolean {
  if (!a) return false
  const x = new Uint8Array(a)
  return x.length === b.length && x.every((v, i) => v === b[i])
}

/**
 * This device's subscription under the server's CURRENT key. 1.68 (spec G20): one made with an older key is useless
 * (the push service refuses what the new key signs) — it is unsubscribed and replaced. A key that cannot be read
 * (offline, push not set up) keeps whatever is there.
 */
async function subscribe(reg: ServiceWorkerRegistration): Promise<PushSubscription | null> {
  const have = await reg.pushManager.getSubscription()
  let key: Uint8Array | null = null
  try {
    const r = await fetch('/api/push')
    const k = r.ok ? ((await r.json()) as { publicKey?: string | null }).publicKey : null
    key = k ? keyBytes(k) : null
  } catch { key = null }
  if (!key) return have   // push is not set up on the server yet, or no answer: keep what is there
  if (have && sameKey(have.options?.applicationServerKey, key)) return have
  try {
    if (have) await have.unsubscribe().catch(() => false)   // the old endpoint drops at its first send (404/410)
    return await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: key as BufferSource })
  } catch { return null }
}

export async function pushState(): Promise<PushState> {
  if (!supported()) return 'unsupported'
  if (Notification.permission === 'denied') return 'denied'
  if (Notification.permission !== 'granted') return 'off'
  const sub = await (await registration())?.pushManager.getSubscription()
  return sub ? 'on' : 'off'
}

/** From a button only: asks permission, subscribes, tells the server. */
export async function enablePush(lang: 'ka' | 'ru'): Promise<boolean> {
  if (!supported()) return false
  if ((await Notification.requestPermission()) !== 'granted') return false
  const reg = await registration()
  const sub = reg ? await subscribe(reg) : null
  return sub ? send(sub, lang) : false
}

/** On open: permission already granted → make sure the server has this device's current keys. Never prompts. */
export async function resyncPush(lang: 'ka' | 'ru'): Promise<void> {
  if (!supported() || Notification.permission !== 'granted') return
  const reg = await registration()
  const sub = reg ? await subscribe(reg) : null
  if (sub) await send(sub, lang)
}

/** Before a sign-out: the server row first (it needs the session), then the browser's subscription. Best effort. */
export async function forgetPushOnThisDevice(): Promise<void> {
  if (!supported()) return
  const sub = await (await registration())?.pushManager.getSubscription()
  if (!sub) return
  await Promise.race([post({ action: 'unsubscribe', endpoint: sub.endpoint }), new Promise((r) => setTimeout(r, 1500))])
  await sub.unsubscribe().catch(() => false)
}
